import type {
  JWTPayload,
  LoginInitiateResponse,
} from "@its-engineering-and-development/jwt-auth-client";
import { eventsAdminAuthClient } from "@app/api/auth-client";
import {
  authClientId,
  authRefreshBufferSeconds,
  authSiteName,
} from "@app/lib/auth/auth-config";
import { authTokenStore } from "@app/lib/auth/token-store";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  companyId: string;
  organizationId: string;
  roles: JWTPayload["roles"];
};

type OneTimeCodeExchangeResponse = {
  accessToken: string;
};

function mapPayloadToUser(payload: JWTPayload | null): AuthUser | null {
  if (!payload) {
    return null;
  }

  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
    companyId: payload.companyGuid,
    organizationId: payload.companyGuid,
    roles: payload.roles,
  };
}

function getTokenExpiryTimestamp(token: string): number | null {
  const payload = eventsAdminAuthClient.decodeToken(token);

  if (!payload) {
    return null;
  }

  return payload.exp * 1000;
}

function isOneTimeCodeExchangeResponse(
  value: unknown,
): value is OneTimeCodeExchangeResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const maybeResponse = value as Record<string, unknown>;

  if (typeof maybeResponse.accessToken !== "string") {
    return false;
  }

  return true;
}

export type ClearAuthSessionOptions = {
  /**
   * When true (default), revoke the current session's refresh token server-side.
   * Pass `false` only when the server has already rejected this refresh token
   * (e.g., a failed refresh) to avoid a guaranteed-failing revoke call.
   */
  revoke?: boolean;
};

export function clearAuthSession(options: ClearAuthSessionOptions = {}): void {
  const { revoke = true } = options;
  // Revoke only the current session's refresh token, not every token issued to
  // the user across devices/sessions (which is what `revokeAll()` does).
  const refreshToken = authTokenStore.getRefreshToken();

  // Clear local tokens first so the auth client's request interceptor cannot
  // attach a stale (expired) bearer to the revoke request. The `/revoke`
  // endpoint accepts an unauthenticated request carrying the refresh token.
  eventsAdminAuthClient.clearTokens();

  if (revoke && refreshToken) {
    // Fire-and-forget: local cleanup has already happened; revoke is best-effort.
    void eventsAdminAuthClient.revokeSingle(refreshToken).catch((error) => {
      console.warn("[auth] Refresh token revocation failed", error);
    });
  }
}

export function getStoredAccessToken(): string | null {
  return eventsAdminAuthClient.getAccessToken();
}

export function getCurrentAuthUser(): AuthUser | null {
  return mapPayloadToUser(eventsAdminAuthClient.decodeToken());
}

export function isTokenNearExpiry(
  token: string,
  refreshWindowSeconds = authRefreshBufferSeconds,
): boolean {
  const expiryTimestamp = getTokenExpiryTimestamp(token);

  if (!expiryTimestamp) {
    return true;
  }

  const refreshWindowStart = expiryTimestamp - refreshWindowSeconds * 1000;
  return refreshWindowStart <= Date.now();
}

export async function refreshAccessToken(): Promise<string> {
  // The auth client's request interceptor attaches `Authorization: Bearer <accessToken>`
  // to every non-public request, and `/refresh` is not on its public list. When the
  // access token is expired, the API Gateway authorizer rejects POST /refresh with 403
  // before the refresh handler runs. Temporarily remove the stored access token so the
  // interceptor cannot attach a stale bearer; the refresh token in the body is enough.
  const previousAccessToken = eventsAdminAuthClient.getAccessToken();
  if (previousAccessToken) {
    authTokenStore.setAccessToken("");
  }

  try {
    const response = await eventsAdminAuthClient.refresh({
      authProvider: "cognito",
    });
    return response.accessToken;
  } catch (error) {
    // The server just rejected this refresh token; skip the revoke call since
    // it is guaranteed to fail and would only add noise to the logs.
    clearAuthSession({ revoke: false });
    throw error;
  }
}

export async function ensureFreshAccessToken(
  forceRefresh = false,
): Promise<string | null> {
  const currentToken = eventsAdminAuthClient.getAccessToken();
  const refreshToken = authTokenStore.getRefreshToken();

  if (!currentToken) {
    if (!refreshToken) {
      return null;
    }

    try {
      return await refreshAccessToken();
    } catch {
      return null;
    }
  }

  if (
    !forceRefresh &&
    !eventsAdminAuthClient.isTokenExpired(currentToken) &&
    !isTokenNearExpiry(currentToken)
  ) {
    return currentToken;
  }

  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

export async function initiateLogin(
  email: string,
): Promise<LoginInitiateResponse> {
  if (!authSiteName) {
    throw new Error("VITE_SITE_NAME must be configured before logging in.");
  }
  return eventsAdminAuthClient.loginInitiate({
    email,
    siteName: authSiteName,
  });
}

export async function verifyMfaCode(
  mfaTicket: string,
  code: string,
): Promise<void> {
  await eventsAdminAuthClient.mfaVerify({ mfaTicket, code });
}

export async function exchangeOneTimeCode(code: string): Promise<string> {
  const response = await eventsAdminAuthClient.exchangeOneTimeCode(
    code,
    authClientId,
  );

  if (!isOneTimeCodeExchangeResponse(response)) {
    throw new Error(
      "The auth service returned an invalid one-time code exchange response.",
    );
  }

  eventsAdminAuthClient.setTokens(response.accessToken);
  return response.accessToken;
}
